/**
 * The 30-day notice a paying tenant gives before a monthly parker leaves.
 * Since 2026-10-16 (Austin): the end date can be any day from today and access
 * ends then; billing runs 30 days from the notice, and the portal shows it.
 *
 * The API enforces this (api/src/tenants/removal_notice.ts, which also explains
 * who counts as paying); this file only lets the portal stop offering an action
 * the server would refuse, so a manager sees the earliest date up front rather
 * than a refusal after they pick one. The dates here come from the browser's
 * clock and the server's from the lot's, so near midnight the two can differ by
 * a day. They only gate the form; the billed-through date a tenant is told
 * always comes from the server (previewPaidNotice in ./api), never from here.
 */
import type { TenantInfo } from './api'
import { todayCalendarDate } from './accountState'

export const PAID_NOTICE_DAYS = 30

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** Mirrors `tenantPaysMonthly` on the API: purchased and overage pay, an unset model is unknown. */
export function tenantPaysMonthly(tenant: Pick<TenantInfo, 'permitModel'> | null | undefined): boolean {
  return tenant?.permitModel === 'purchased' || tenant?.permitModel === 'overage'
}

/** Calendar-date arithmetic with no clock or zone in it: YYYY-MM-DD in, YYYY-MM-DD out. */
export function addDays(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`
}

/** The earliest end date a paying tenant may set. */
export function earliestPaidEndDate(today: string = todayCalendarDate()): string {
  return addDays(today, PAID_NOTICE_DAYS)
}

export function friendlyDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  return `${MONTHS[m - 1]} ${d}, ${y}`
}

/**
 * Whether an end date in the Edit form is acceptable. A paying tenant may leave
 * the date as it already is (so an unrelated edit still saves), clear it, or
 * move it to the earliest allowed date or later.
 */
export function endDateAllowed(opts: {
  paid: boolean
  noEndDate: boolean
  validUntil: string
  original: string
  today?: string
}): boolean {
  if (opts.noEndDate) return true
  if (!/^\d{4}-\d{2}-\d{2}$/.test(opts.validUntil)) return false
  const today = opts.today ?? todayCalendarDate()
  // Austin, 2026-10-16: a paying tenant may pick any date from today on (access
  // ends then); billing runs 30 days from the notice (the server's preview).
  if (!opts.paid) return opts.validUntil >= today
  return opts.validUntil === opts.original || opts.validUntil >= today
}

/** Whether a paying tenant may remove this parker right now: only once their end date has passed. */
export function removableNow(opts: { paid: boolean; validUntil: string; today?: string }): boolean {
  if (!opts.paid) return true
  const today = opts.today ?? todayCalendarDate()
  return !!opts.validUntil && opts.validUntil < today
}
